import { FilterList } from '@mui/icons-material';
import {
  Button,
  Card,
  Checkbox,
  Chip,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  Grow,
  Popper,
  Typography,
} from '@mui/material';
import { isArray, xor } from 'lodash';
import type { Dispatch, FC, SetStateAction } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { CategoryStatus } from '../../../../../../__generated__/queries-web';
import { useQueryParams } from '../../../../../../utils/useUriHelpers';
import { ALLOWED_CATEGORY_STATUES } from '../../../../constants';
import { DataTestIdListPage } from '../../../../tests/constants';
import { getActiveFilterQuantity } from '../../utils';
import {
  INITIAL_QUERY_PARAMS,
  FILTER_TITLE,
  RESET_FILTER_TITLE,
} from './constants';
import { useStyles } from './styles';

interface FilterProps {
  isFilterOpen: boolean;
  setIsFilterOpen: Dispatch<SetStateAction<boolean>>;
}
export const Filter: FC<FilterProps> = ({ isFilterOpen, setIsFilterOpen }) => {
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const classes = useStyles();

  const searchParams = useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  const { queryParams, setQueryParam } = useQueryParams(INITIAL_QUERY_PARAMS);

  // Temporary filter state for confirmation
  const [tempFilters, setTempFilters] = useState({
    filterStatus: queryParams.filterStatus,
  });

  const activeFilterQuantity = useMemo(
    () => getActiveFilterQuantity(queryParams),
    [queryParams],
  );

  // Handle temporary filter changes (not applied yet)
  const handleTempFilterChange = useCallback(
    <K extends keyof typeof tempFilters, T>(filterName: K, filterValue: T) => {
      setTempFilters((prev) => ({
        ...prev,
        [filterName]: filterValue,
      }));
    },
    [],
  );

  // Apply the temporary filters (confirm action)
  const handleApplyFilters = useCallback(() => {
    setQueryParam('filterStatus', tempFilters.filterStatus, 'replace');
    setIsFilterOpen(false);
  }, [tempFilters, setQueryParam, setIsFilterOpen]);

  const handleInitFilter = useCallback(
    (forceReinit?: boolean) => {
      Object.keys(INITIAL_QUERY_PARAMS).forEach((filterKey) => {
        if (forceReinit || !searchParams.get(filterKey)) {
          const key = filterKey as keyof typeof INITIAL_QUERY_PARAMS;
          const value = INITIAL_QUERY_PARAMS[key];

          // We won't showing Archived and Deleted categories by default
          const filteredValue =
            !forceReinit && isArray(value)
              ? value.filter(
                  (status) =>
                    status !== CategoryStatus.Archived &&
                    status !== CategoryStatus.Deleted,
                )
              : value;

          setQueryParam(key, filteredValue, 'replace');
        }
      });
    },
    [searchParams, setQueryParam],
  );

  useEffect(() => {
    handleInitFilter();

    // Clean up existing URL parameters that might contain Deleted
    const currentFilterStatus = searchParams.getAll('filterStatus');
    if (currentFilterStatus.includes(CategoryStatus.Deleted)) {
      const cleanedFilterStatus = currentFilterStatus.filter(
        (status) => status !== CategoryStatus.Deleted,
      );
      setQueryParam('filterStatus', cleanedFilterStatus, 'replace');
    }
    // eslint-disable-next-line
  }, [history.location.search]);

  // Sync temporary filters when filter panel opens
  useEffect(() => {
    if (isFilterOpen) {
      setTempFilters({
        filterStatus: queryParams.filterStatus,
      });
    }
  }, [isFilterOpen, queryParams.filterStatus]);

  return (
    <div className={classes.wrapper} ref={filterButtonRef}>
      <Chip
        className={classes.filterButton}
        color={activeFilterQuantity ? 'primary' : 'default'}
        data-testid={DataTestIdListPage.FILTER_BUTTON}
        icon={<FilterList />}
        label={`Filtres (${activeFilterQuantity})`}
        onClick={() => {
          setIsFilterOpen(!isFilterOpen);
        }}
      />

      <Popper
        anchorEl={filterButtonRef.current}
        className={classes.popper}
        open={isFilterOpen}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'right center' : 'center bottom',
            }}
          >
            <div>
              <ClickAwayListener
                mouseEvent="onMouseDown"
                touchEvent="onTouchStart"
                onClickAway={(event: any) => {
                  if (!filterButtonRef.current?.contains(event.target)) {
                    setIsFilterOpen(false);
                  }
                }}
              >
                <Card classes={{ root: classes.card }} raised>
                  <Typography variant="subtitle1">{FILTER_TITLE}</Typography>

                  {/* Status */}
                  <div className={classes.section}>
                    {ALLOWED_CATEGORY_STATUES.map((status) => {
                      const isChecked = !!tempFilters?.filterStatus?.includes(
                        status.id,
                      );
                      const isDisabled = !!(
                        isChecked &&
                        tempFilters?.filterStatus &&
                        !Array.isArray(tempFilters.filterStatus)
                      );

                      return (
                        <FormControlLabel
                          className={classes.formControl}
                          label={status.label}
                          key={status.id}
                          control={
                            <Checkbox
                              checked={isChecked}
                              color="primary"
                              data-testid={status.id}
                              disabled={isDisabled}
                              onChange={() => {
                                handleTempFilterChange(
                                  'filterStatus',
                                  xor(
                                    isArray(tempFilters.filterStatus)
                                      ? [...tempFilters.filterStatus]
                                      : [tempFilters.filterStatus],
                                    [status.id],
                                  ),
                                );
                              }}
                            />
                          }
                        />
                      );
                    })}
                  </div>

                  <Divider />

                  {/* Action Buttons */}
                  <div
                    className={classes.section}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      paddingTop: '8px',
                    }}
                  >
                    <Button
                      color="primary"
                      variant="contained"
                      size="medium"
                      onClick={handleApplyFilters}
                      sx={{
                        minWidth: '120px',
                        textTransform: 'none',
                        fontWeight: 500,
                        height: '36px',
                        padding: '6px 16px',
                        boxShadow: 'none',
                        border: '1px solid var(--mui-palette-primary-main)',
                        '&:hover': {
                          boxShadow: 'none',
                          backgroundColor: 'var(--mui-palette-primary-dark)',
                        },
                      }}
                    >
                      Filtrer
                    </Button>

                    <Button
                      color="secondary"
                      variant="outlined"
                      size="medium"
                      onClick={() => {
                        setTempFilters({
                          filterStatus: INITIAL_QUERY_PARAMS.filterStatus,
                        });
                      }}
                      sx={{
                        minWidth: '120px',
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      {RESET_FILTER_TITLE}
                    </Button>
                  </div>
                </Card>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
